import type { BuildQuote, ContentFormat, DraftVoiceDto, Manner, MannerChoice, StartBuildInput, VoiceoverDto } from '@app/shared';
import type { Dictionary } from '../i18n';
import { countOf, creditsCount } from './format';

/*
 * «Озвучка» (spec 5): which voice and manner the screen starts on, what ▶ plays,
 * which ready take fits, what «Собрать» does and the total — pure, the page draws it.
 * A take never starts by itself: only «Послушать с моим текстом» and «Собрать» start one.
 */

export type VoiceChoice = { source: 'stock'; voice: string } | { source: 'clone' } | { source: 'own' };

const firstStock = (state: DraftVoiceDto): VoiceChoice | null =>
  state.stock[0] ? { source: 'stock', voice: state.stock[0].id } : null;

/**
 * The person's choice while it still exists; else the clone when it is ready,
 * else the first ready-made voice (spec 5.1: no sample is asked by default). A
 * stock voice the owner switched off falls back to the first one.
 */
export function defaultVoiceChoice(state: DraftVoiceDto, current?: VoiceChoice): VoiceChoice {
  if (current?.source === 'stock') {
    if (state.stock.some((v) => v.id === current.voice)) return current;
    return firstStock(state) ?? { source: 'clone' };
  }
  if (current) return current;
  if (state.voice === 'ready') return { source: 'clone' };
  return firstStock(state) ?? { source: 'clone' };
}

/** The voice and the manner chip of a take. */
function choiceOfTake(take: VoiceoverDto): { choice: VoiceChoice; manner: MannerChoice } {
  const choice: VoiceChoice =
    take.source === 'stock' ? { source: 'stock', voice: take.stockVoice ?? '' } : { source: take.source };
  return { choice, manner: take.mannerAuto || take.manner === null ? 'auto' : take.manner };
}

/**
 * What a visit to «Озвучка» starts on: what the person chose before — the take at
 * work, else the newest ready take of this text — so leaving and coming back keeps
 * the voice and the manner, and «Собрать» finds that take (Review Focus 2). A voice
 * switched off since, or no take yet: the default. `asked` (the link's
 * ?voice=own) wins.
 */
export function startingChoice(
  state: DraftVoiceDto,
  asked?: VoiceChoice,
): { choice: VoiceChoice; manner: MannerChoice } {
  if (asked) return { choice: defaultVoiceChoice(state, asked), manner: 'auto' };
  const last = state.working ?? state.ready[0] ?? null;
  if (last) {
    const from = choiceOfTake(last);
    const { choice } = from;
    const gone = choice.source === 'stock' && !state.stock.some((v) => v.id === choice.voice);
    if (!gone) return from;
  }
  return { choice: defaultVoiceChoice(state), manner: 'auto' };
}

/** «Само подберёт» is the manner the brain suggested for this text, calm without one. */
export function resolvedManner(choice: MannerChoice, draftManner: Manner | null): Manner {
  return choice === 'auto' ? (draftManner ?? 'calm') : choice;
}

export type Sample = { kind: 'url'; url: string } | { kind: 'clone'; manner: Manner };

/** What ▶ plays (spec 5.2, 5.3): a stock voice's file of the manner, the clone's own once made; none for an own reading. */
export function sampleOf(state: DraftVoiceDto, choice: VoiceChoice, manner: Manner): Sample | null {
  if (choice.source === 'stock') {
    const url = state.stock.find((v) => v.id === choice.voice)?.samples[manner] ?? null;
    return url ? { kind: 'url', url } : null;
  }
  if (choice.source === 'clone') return state.voice === 'ready' && state.cloneSamples ? { kind: 'clone', manner } : null;
  return null;
}

/** «образец: Алина, энергично» — whose sample and which manner ▶ plays; none when nothing can play. */
export function sampleLine(state: DraftVoiceDto, choice: VoiceChoice, manner: Manner, t: Dictionary): string | null {
  if (choice.source === 'own' || sampleOf(state, choice, manner) === null) return null;
  const who =
    choice.source === 'stock' ? state.stock.find((v) => v.id === choice.voice)?.name : t.voiceScreen.sampleMine;
  return who ? t.voiceScreen.sample(who, t.voiceover.manners[manner].toLowerCase()) : null;
}

/** A ready take of this text for the choice: played and built without a new call (spec 5.4). */
export function readyTakeOf(state: DraftVoiceDto, choice: VoiceChoice, manner: Manner): VoiceoverDto | null {
  if (choice.source === 'own') return state.own?.status === 'ready' ? state.own : null;
  const fits = (v: VoiceoverDto) =>
    v.status === 'ready' &&
    v.manner === manner &&
    (choice.source === 'stock' ? v.source === 'stock' && v.stockVoice === choice.voice : v.source === 'clone');
  return state.ready.find(fits) ?? null;
}

/** «осталось 3 из 5 озвучек для этого ролика»; null without a limit. */
export function takesLine(state: DraftVoiceDto, t: Dictionary): string | null {
  if (state.takesLimit === null) return null;
  return t.voiceScreen.takesLeft(state.takesLeft ?? 0, state.takesLimit);
}

export type BuildStep =
  | { kind: 'build'; voiceoverId: number }
  | { kind: 'voice_first' }
  | { kind: 'wait'; voiceoverId: number }
  | { kind: 'record_own' }
  | { kind: 'no_clone' }
  | { kind: 'limit' };

/**
 * What «Собрать» does (spec 5.6): builds with a ready take at once; waits for a
 * take at work (one take at a time per draft); else voices first — unless no
 * takes are left, the clone is not ready, or an own reading is not recorded.
 */
export function buildStep(state: DraftVoiceDto, choice: VoiceChoice, manner: Manner): BuildStep {
  const ready = readyTakeOf(state, choice, manner);
  if (ready) return { kind: 'build', voiceoverId: ready.id };
  if (choice.source === 'own') return { kind: 'record_own' };
  if (choice.source === 'clone' && state.voice !== 'ready') return { kind: 'no_clone' };
  if (state.working) return { kind: 'wait', voiceoverId: state.working.id };
  if (state.takesLeft === 0) return { kind: 'limit' };
  return { kind: 'voice_first' };
}

/** The voice and manner «Собрать» sends when no take of them is ready (0710). */
export type TakeChoice = NonNullable<StartBuildInput['take']>;

/** What «Собрать» asks the server to voice and wait for; none for an own reading. */
export function takeChoiceOf(choice: VoiceChoice, manner: MannerChoice): TakeChoice | null {
  if (choice.source === 'stock') return { source: 'stock', voice: choice.voice, manner };
  if (choice.source === 'clone') return { source: 'clone', manner };
  return null;
}

/** «Итого — 30 кредитов», or «Итого — бесплатно, пересборка 1 из 2». */
export function totalLine(quote: BuildQuote, freeRebuilds: number, t: Dictionary): string {
  const v = t.voiceScreen;
  return quote.free ? v.totalFree(freeRebuilds - quote.freeLeft + 1, freeRebuilds) : v.total(creditsCount(quote.price));
}

/** «На балансе 270 кредитов — хватит на 9 рилсов»; just the balance when the build is free. */
export function affordLine(balance: number, price: number, format: ContentFormat, t: Dictionary): string {
  const v = t.voiceScreen;
  if (price <= 0) return v.balance(creditsCount(balance));
  const count = Math.floor(balance / price);
  return v.afford(creditsCount(balance), countOf(count, format === 'reel' ? t.units.reels : t.units.carousels));
}
